/* global Word */

/**
 * applyHeroBgImage.ts
 *
 * Finds the picture content control tagged "hero_bg_image" and replaces its
 * content with the supplied base64 image.
 *
 * The content control lives inside a floating text-box shape on the first page.
 * document.contentControls only scans body flow - it does NOT reach into shapes.
 */

import { WordApiError } from "../../core/errors";
import { logger } from "../../core/logging";
import { cmToPt } from "../../core/utils/math";

const HERO_TAG = "hero_bg_image";
const HERO_BOX_WIDTH_PT = cmToPt(21);
const HERO_BOX_HEIGHT_PT = cmToPt(5);

export type ApplyBgImageResult =
  | { success: true }
  | { success: false; reason: "not_found" | "word_api_error"; message: string };

/**
 * Applies a base64-encoded image to the hero_bg_image placeholder.
 * Pass the raw base64 string or a data-URI - both are handled.
 */
export async function applyHeroBgImage(base64Image: string): Promise<ApplyBgImageResult> {
  try {
    return await Word.run(async (context) => {
      const cleanBase64 = stripDataUri(base64Image);

      if (!cleanBase64 || cleanBase64.length < 100) {
        throw new Error("Invalid base64 image data - conversion may have failed.");
      }

      const found =
        (await strategy1DocumentBody(context, cleanBase64)) ||
        (await strategy2BodyShapes(context, cleanBase64)) ||
        (await strategy3Sections(context, cleanBase64));

      if (!found) {
        throw new Error(
          `No content control with tag "${HERO_TAG}" found. Open the document from the asset-allocation template and try again.`
        );
      }

      logger.info("Hero background image applied", { tag: HERO_TAG });
      return { success: true };
    });
  } catch (err) {
    if (err instanceof Error && err.message.includes(HERO_TAG)) throw err;
    if (err instanceof Error && err.message.includes("Invalid base64")) throw err;

    const wordErr = WordApiError.fromOfficeError(err, { operation: "applyHeroBgImage" });
    logger.error("Failed to apply hero background image", { error: wordErr });
    return {
      success: false,
      reason: "word_api_error",
      message: wordErr.message,
    };
  }
}

async function strategy1DocumentBody(
  context: Word.RequestContext,
  base64: string
): Promise<boolean> {
  try {
    const match = await findHeroContentControlInBody(context, context.document.body);
    if (!match) return false;

    logger.info("[applyHeroBgImage] Strategy 1 found control", {});
    await insertImage(context, match, base64);
    return true;
  } catch (err) {
    logger.warn("[applyHeroBgImage] Strategy 1 failed", { err: String(err) });
    return false;
  }
}

async function strategy2BodyShapes(
  context: Word.RequestContext,
  base64: string
): Promise<boolean> {
  try {
    const body = context.document.body;

    // Use a type assertion - older @types/office-js may not expose .shapes
    const shapesCollection = (body as any).shapes as Word.ShapeCollection | undefined;
    if (!shapesCollection) {
      logger.warn("[applyHeroBgImage] body.shapes not available in this API set", {});
      return false;
    }

    shapesCollection.load("items/type");
    await context.sync();

    return await searchShapeCollectionForHero(context, shapesCollection.items, base64);
  } catch (err) {
    logger.warn("[applyHeroBgImage] Strategy 2 failed", { err: String(err) });
    return false;
  }
}

async function strategy3Sections(
  context: Word.RequestContext,
  base64: string
): Promise<boolean> {
  try {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();

    for (const section of sections.items) {
      const bodies: Word.Body[] = [];

      const addBodySafe = (fn: () => Word.Body) => {
        try {
          bodies.push(fn());
        } catch (e) {
          // ignore unsupported header/footer types in older Word versions
        }
      };

      addBodySafe(() => section.getHeader(Word.HeaderFooterType.primary));
      addBodySafe(() => section.getHeader(Word.HeaderFooterType.firstPage));
      addBodySafe(() => section.getHeader(Word.HeaderFooterType.evenPages));
      addBodySafe(() => section.getFooter(Word.HeaderFooterType.primary));
      addBodySafe(() => section.getFooter(Word.HeaderFooterType.firstPage));
      addBodySafe(() => section.getFooter(Word.HeaderFooterType.evenPages));
      addBodySafe(() => section.body);

      for (const sectionBody of bodies) {
        try {
          // 1. Check body flow of this section part
          const match = await findHeroContentControlInBody(context, sectionBody);
          if (match) {
            logger.info("[applyHeroBgImage] Strategy 3 found control in section body flow", {});
            await insertImage(context, match, base64);
            return true;
          }

          // 2. Check shapes inside this section part (e.g. textboxes in headers)
          const shapesCollection = (sectionBody as any).shapes as Word.ShapeCollection | undefined;
          if (shapesCollection) {
            shapesCollection.load("items/type");
            await context.sync();
            const foundInShapes = await searchShapeCollectionForHero(context, shapesCollection.items, base64);
            if (foundInShapes) {
              logger.info("[applyHeroBgImage] Strategy 3 found control in section shapes", {});
              return true;
            }
          }
        } catch {
          continue;
        }
      }
    }

    return false;
  } catch (err) {
    logger.warn("[applyHeroBgImage] Strategy 3 failed", { err: String(err) });
    return false;
  }
}

/**
 * Inserts an image into a content control using InsertLocation.replace.
 * Never call control.clear() before this - it corrupts picture content controls.
 */
async function insertImage(context: Word.RequestContext, control: Word.ContentControl, base64: string): Promise<void> {
  const existingPictures = control.getRange().inlinePictures;
  existingPictures.load("items");
  await context.sync();
  
  let image: Word.InlinePicture;
  if (existingPictures.items.length > 0) {
    // If an image already exists, replace it directly in-place to prevent stacking
    image = existingPictures.items[0].insertInlinePictureFromBase64(base64, Word.InsertLocation.replace);
  } else {
    // Otherwise, insert it into the empty control range
    image = control.getRange().insertInlinePictureFromBase64(base64, Word.InsertLocation.replace);
  }
  await context.sync();
  
  try {
    image.lockAspectRatio = false;
    image.width = HERO_BOX_WIDTH_PT;
    image.height = HERO_BOX_HEIGHT_PT;
    await context.sync();
  } catch (err) {
    logger.warn("[applyHeroBgImage] Failed to apply custom dimensions to the inserted image, using default placeholder dimensions", { err: String(err) });
  }
}

async function searchShapeCollectionForHero(
  context: Word.RequestContext,
  shapes: Word.Shape[],
  base64: string
): Promise<boolean> {
  for (const shape of shapes) {
    try {
      const shapeBody = (shape as any).body as Word.Body | undefined;
      if (shapeBody) {
        const match = await findHeroContentControlInBody(context, shapeBody);
        if (match) {
          logger.info("[applyHeroBgImage] Found control inside shape body", {});
          await insertImage(context, match, base64);
          return true;
        }
      }
    } catch (err) {
      // Ignore errors from shapes that do not support a body
    }

    if (shape.type === Word.ShapeType.group) {
      const shapeGroup = (shape as any).shapeGroup as Word.ShapeGroup | undefined;
      const nestedShapes = shapeGroup?.shapes;
      if (nestedShapes) {
        nestedShapes.load("items/type");
        await context.sync();

        if (await searchShapeCollectionForHero(context, nestedShapes.items, base64)) {
          return true;
        }
      }
    }

    if (shape.type === Word.ShapeType.canvas) {
      const canvas = (shape as any).canvas as Word.Canvas | undefined;
      const nestedShapes = canvas?.shapes;
      if (nestedShapes) {
        nestedShapes.load("items/type");
        await context.sync();

        if (await searchShapeCollectionForHero(context, nestedShapes.items, base64)) {
          return true;
        }
      }
    }
  }

  return false;
}

async function findHeroContentControlInBody(
  context: Word.RequestContext,
  body: Word.Body
): Promise<Word.ContentControl | null> {
  const contentControls = body.getContentControls();
  contentControls.load("items/tag");
  await context.sync();

  const match = contentControls.items.find((cc) => cc.tag === HERO_TAG);
  return match ?? null;
}

/**
 * Converts a webpack-bundled image URL to a clean base64 string (no data-URI prefix).
 */
export async function imageUrlToBase64(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image at "${url}" - status ${response.status}`);
  }
  const blob = await response.blob();
  const dataUri = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
  return stripDataUri(dataUri);
}

function stripDataUri(raw: string): string {
  const commaIndex = raw.indexOf(",");
  if (commaIndex !== -1) return raw.slice(commaIndex + 1);
  return raw;
}

export default applyHeroBgImage;